/**
 * UI unificada dos sistemas do PLANO-MESTRE.
 */
import { t } from '../i18n/index.js?v=20260727-atlas-guide-3';
import { TRACK_IDS, TRACKS, upgradeCost, upgradePerks, applyBuy, normalizeUpgrades } from '../upgrades/upgrade-core.mjs';
import { RECIPES, craftItem, canCraft, reviveFossil, normalizeProfessions, FOSSIL_REVIVALS } from '../professions/crafting-core.mjs';
import { normalizeMeta, claimDailyGift, dailyGiftProgress, DAILY_REWARDS, DAILY_GIFT_DAYS, grantPassPoints, progressQuest, QUEST_TEMPLATES, ACHIEVEMENTS, rotateQuests, claimQuest, questsNeedRotation, checkAchievements, logCraftCompleted, logFossilRevival } from '../meta/retention-core.mjs';
import {
  normalizeMonetization, EMERALD_BALL_OFFERS, buyEmeraldBall, BOOST_DEFS,
  buyBoost, boostPrice, activateBoost, VIP_PLANS, applyVipEntitlement, isVip, vipPerks, buyPremiumPass, claimTier,
  activeBoostMult, SEASON_PREMIUM_COST, SEASON_TIERS,
} from '../monetization/monetization-core.mjs?v=20260723-monetization-1';
import { EMERALD_PACKAGES, quoteEmeraldPurchase, formatBrlFromCents } from '../monetization/emerald-pricing.mjs?v=20260723-monetization-1';
import { GYMS, canChallengeGym, resolveGymBattle, ELITE_FOUR, CHAMPION, canChallengeElite, resolveEliteBattle, canChallengeChampion, resolveChampionBattle } from '../gym/gym-data.mjs';
import { CLAN_RANKS } from '../clan/clan-core.mjs';
import { CITY_NPCS } from '../city/npcs.js';
import { ITEM_BY_SLUG } from '../items-data.js';
import { emeraldMark, idlecoinMark, uiIcon } from '../ui-icons.js';
import { RELEASE_FEATURES, unavailableFeatureMessage } from '../release/feature-gates.mjs';

const esc = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const em = (size = 18) => emeraldMark(size);
const coin = (size = 14) => idlecoinMark(size);
const prettify = (slug) => String(slug || '').replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

let hooks = {
  getState: () => ({}),
  save: () => {},
  showToast: () => {},
  formatNumber: (n) => String(n),
  teamPower: () => 0,
  refreshEconomy: () => {},
  openMarket: () => {},
  openFishing: () => {},
  beginEmeraldCheckout: async () => {},
  spendEmerald: async () => {},
  purchasePremiumProduct: async () => {},
  activatePremiumBoost: async () => {},
  purchaseVip: async () => {},
  purchaseSeasonPass: async () => {},
  claimSeasonTier: async () => {},
  claimDailyQuest: async () => {},
  reportDailyQuestProgress: async () => {},
  claimRaidRewards: async () => {},
  fetchRaid: async () => null,
  fetchClan: async () => null,
  joinClanOnServer: async () => null,
  skipClanRankOnServer: async () => null,
  fetchDailyQuests: async () => null,
  claimDailyGift: async () => null,
  fetchDailyGift: async () => null,
  acceptWallet: () => {},
  applyInventory: () => {},
  applyPremiumBoosts: () => {},
  trackEvent: () => {},
  userId: () => 'local',
  refreshDailyGiftBadge: () => {},
  serverEconomyReady: () => false,
  onPanelOpen: () => {},
};

const BOOST_PRODUCT_IDS = Object.freeze({
  trainerXp: 'boost-trainer-xp',
  pokemonXp: 'boost-pokemon-xp',
  loot: 'boost-loot',
  capture: 'boost-capture',
  shinyLure: 'boost-shiny',
});

const BOOST_ICON_NAMES = Object.freeze({
  trainerXp: 'award',
  pokemonXp: 'dna',
  loot: 'backpack',
  capture: 'pokeball',
  shinyLure: 'sparkles',
  idlecoin: 'diamond',
});

const BOOST_ACCENTS = Object.freeze({
  trainerXp: '#f1c40f',
  pokemonXp: '#5dade2',
  loot: '#e5ae65',
  capture: '#ff6b67',
  shinyLure: '#c68cff',
  idlecoin: '#f3d35f',
});

let emeraldTermsAccepted = false;
let emeraldCheckoutBusy = false;
let emeraldShopTab = 'packages';
let remoteClan = null;
let remoteClanLoaded = false;
let remoteClanLoading = false;
let remoteRaid = null;
let remoteRaidLoaded = false;

function boostIcon(key, size = 28) {
  if (key === 'idlecoin') return idlecoinMark(size);
  return `<span class="boost-icon" style="--boost-accent:${BOOST_ACCENTS[key] || '#f1c40f'}">${uiIcon(BOOST_ICON_NAMES[key] || 'sparkles')}</span>`;
}

function itemThumb(slug, size = 28) {
  const it = ITEM_BY_SLUG[slug];
  if (!it?.img) return `<span class="pass-reward-fallback">${esc(prettify(slug).slice(0, 1))}</span>`;
  return `<img class="shop-item-icon pass-reward-icon" src="${it.img}" alt="${esc(it.name || slug)}" width="${size}" height="${size}" loading="lazy">`;
}

function formatBoostRemaining(expiresAt, now = Date.now()) {
  const ms = Math.max(0, Number(expiresAt) - now);
  const hours = Math.floor(ms / 3600000);
  const mins = Math.floor((ms % 3600000) / 60000);
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

function rewardVisual(reward) {
  if (!reward) return '';
  if (reward.type === 'coins') return coin(30);
  if (reward.type === 'emerald') return em(30);
  if (reward.type === 'boost') return boostIcon(reward.key, 30);
  if (reward.type === 'item' && reward.slug) return itemThumb(reward.slug, 32);
  return '';
}

export function initMasterSystemsUI(next = {}) {
  hooks = { ...hooks, ...next };
  ensureOverlayRoot();
  return { openPanel, CITY_NPCS };
}

function ensureOverlayRoot() {
  if (document.getElementById('masterSystemsRoot')) return;
  const root = document.createElement('div');
  root.id = 'masterSystemsRoot';
  root.innerHTML = `<div class="city-dialog hidden" id="masterSystemsDialog"><div class="master-systems-card" role="dialog" aria-modal="true" aria-labelledby="masterSystemsTitle"><button type="button" class="dialog-close" id="masterSystemsClose">&times;</button><header class="master-head"><span class="eyebrow" id="masterSystemsEyebrow"></span><h2 id="masterSystemsTitle"></h2><p id="masterSystemsSub"></p></header><div id="masterSystemsBody" class="master-systems-body"></div></div></div>`;
  document.body.appendChild(root);
  document.getElementById('masterSystemsClose')?.addEventListener('click', closePanel);
  document.getElementById('masterSystemsDialog')?.addEventListener('click', (e) => { if (e.target.id === 'masterSystemsDialog') closePanel(); });
}

function closePanel() { document.getElementById('masterSystemsDialog')?.classList.add('hidden'); }
function openDialog(title, sub, html, opts = {}) {
  ensureOverlayRoot();
  document.getElementById('masterSystemsEyebrow').textContent = opts.eyebrow || t('nav.systems') || 'Sistemas';
  document.getElementById('masterSystemsTitle').textContent = title;
  document.getElementById('masterSystemsSub').textContent = sub || '';
  const body = document.getElementById('masterSystemsBody');
  body.innerHTML = `${opts.back ? `<button type="button" class="master-back" data-open-panel="hub">← Voltar</button>` : ''}${html}`;
  body.scrollTop = 0;
  document.getElementById('masterSystemsDialog').classList.remove('hidden');
  wireBody();
}
function state() { return hooks.getState(); }
function money(n) { return hooks.formatNumber(Math.round(Number(n) || 0)); }
function hubCard(id, icon, label, desc, status = '', live = false) {
  return `<button type="button" class="master-hub-btn ${live ? 'live' : ''}" data-open-panel="${id}">
    <span class="master-hub-icon">${icon}</span>
    <span class="master-hub-copy"><b>${esc(label)}</b><small>${esc(desc)}</small></span>
    ${status ? `<em class="master-hub-status">${status}</em>` : ''}
  </button>`;
}

export function openPanel(panelId) {
  const unavailable = {
    raid: 'raids',
    clan: 'clans',
    clanHerald: 'clans',
    pass: 'seasonPass',
    mine: 'miner',
    miner: 'miner',
  }[panelId];
  if (unavailable && !RELEASE_FEATURES[unavailable]) {
    hooks.showToast(unavailableFeatureMessage(unavailable));
    return;
  }
  if (panelId === 'boosts') {
    emeraldShopTab = 'boosts';
    panelId = 'shopEmerald';
  } else if (panelId === 'vip') {
    emeraldShopTab = 'vip';
    panelId = 'shopEmerald';
  } else if (panelId === 'shopEmerald') {
    emeraldShopTab = 'packages';
  }
  const map = {
    hub: renderHub, upgrades: renderUpgrades, craft: renderCraft, cook: () => renderCraft('cook'),
    mine: renderMine, meta: renderMeta, quests: renderMeta, professor: renderMeta,
    shopEmerald: renderEmeraldShop, pass: renderPass, daily: renderDailyGift,
    gym: renderGym, gymLeader: renderGym, clan: renderClan, clanHerald: renderClan, raid: renderRaid,
    blacksmith: renderCraft, apothecary: () => renderCraft('cook'), miner: renderMine,
    fisher: () => hooks.openFishing?.(), auctioneer: () => hooks.openMarket?.(), market: () => hooks.openMarket?.(),
    fish: () => hooks.openFishing?.(), center: renderMeta, shop: () => hooks.openMarket?.(),
  };
  try {
    (map[panelId] || renderHub)();
    hooks.onPanelOpen?.(panelId);
  } catch (error) {
    console.error('master panel failed', panelId, error);
    hooks.showToast(location.hostname === 'localhost' ? `Painel: ${error?.message || error}` : 'Não foi possível abrir este painel.');
  }
}

function renderHub() {
  const s = state();
  normalizeUpgrades(s); normalizeMeta(s); normalizeMonetization(s);
  const lvlTotal = TRACK_IDS.reduce((sum, id) => sum + (s.upgrades[id] || 0), 0);
  const lvlMax = TRACK_IDS.reduce((sum, id) => sum + TRACKS[id].levels, 0);
  const ready = (s.meta.quests?.active || []).filter((q) => q.completed && !q.claimed).length;
  const streak = s.meta?.daily?.streak || 0;
  openDialog(t('systems.hubTitle') || 'Sistemas da cidade', t('systems.hubSub') || 'Economia · retenção · combate', `
    <p class="master-hint">Craft, ginásios, clãs e pesca ficam com os NPCs da cidade — ande até eles. Daily Gift, Boosts/VIP (Loja de Esmeraldas) e Game Pass ficam no menu principal. A dificuldade da caça está no Atlas de Hunt.</p>
    <section class="master-section">
      <small class="eyebrow">Progressão</small>
      <div class="master-hub-grid">
        ${hubCard('upgrades', uiIcon('award'), 'Upgrades', 'Melhorias permanentes', `<b>${lvlTotal}</b>/${lvlMax} níveis`)}
        ${hubCard('meta', uiIcon('book-2'), 'Missões', 'Diárias & coleção', ready ? `<b>${ready}</b> pronta${ready > 1 ? 's' : ''}` : `Streak <b>${streak}</b>`, ready > 0)}
        ${hubCard('craft', uiIcon('hammer'), 'Bancada', 'Blacksmith e receitas', 'Oficinas')}
        ${hubCard('cook', uiIcon('flame'), 'Cozinha', 'Apothecary e poções', 'Oficinas')}
        ${RELEASE_FEATURES.raids ? hubCard('raid', uiIcon('skull'), t('raid.title'), t('raid.hubSub')) : ''}
      </div>
    </section>`);
}

const TRACK_LABELS = {
  lootYield: ['Rendimento de loot', 'Mais drops por derrota'],
  captureLuck: ['Sorte de captura', 'Auto-restock de bolas no Nv.8'],
  offlineCap: ['Teto offline', 'Mais horas de progresso offline'],
};

function renderUpgrades() {
  const s = state(); normalizeUpgrades(s);
  const perks = upgradePerks(s.upgrades);
  openDialog('Upgrades permanentes', 'Sink de IdleCoin · curva ×1.55ⁿ', `
    <p class="master-hint">A cadência da hunt depende do Pokémon e dos golpes equipados.</p>
    <div class="master-perk-row"><span>Loot +${Math.round((perks.lootMultiplier - 1) * 100)}%</span><span>Capt +${Math.round((perks.captureMultiplier - 1) * 100)}%</span><span>Offline +${perks.offlineCapBonusHours}h</span></div>
    <div class="master-list">${TRACK_IDS.map((id) => {
      const lvl = s.upgrades[id] || 0;
      const next = upgradeCost(id, lvl);
      const [label, desc] = TRACK_LABELS[id] || [prettify(id), ''];
      return `<article class="master-row"><div><b>${esc(label)}</b><small>${esc(desc)} · Nv. ${lvl}/${TRACKS[id].levels}</small></div>
        <button type="button" class="master-btn" data-buy-upgrade="${id}" ${next == null || (s.stats?.coins || 0) < next ? 'disabled' : ''}>${next == null ? 'Máx' : `${coin(13)} ${money(next)}`}</button></article>`;
    }).join('')}</div>`, { back: true });
}

function renderCraft(filterProf = null) {
  const s = state(); normalizeProfessions(s);
  const list = RECIPES.filter((r) => !filterProf || r.profession === filterProf);
  openDialog(filterProf === 'cook' ? 'Cozinha' : 'Bancada', 'Craft a partir dos drops', `
    <div class="master-list">${list.map((r) => {
      const ok = canCraft(s, r.id);
      const ings = Object.entries(r.ingredients).map(([slug, qty]) => `${qty}× ${prettify(slug)}`).join(', ');
      return `<article class="master-row"><div><b>${esc(prettify(r.product?.slug || r.id))}</b><small>Lv${r.level} · ${esc(ings)}${r.coinsCost ? ` · ${money(r.coinsCost)} IdleCoin` : ''}</small></div>
        <button type="button" class="master-btn" data-craft="${r.id}" ${ok ? '' : 'disabled'}>Craft</button></article>`;
    }).join('')}</div>
    <section class="master-section"><small class="eyebrow">Fósseis</small>
    <div class="master-list">${Object.entries(FOSSIL_REVIVALS).map(([slug, plan]) => `
      <article class="master-row"><div><b>${esc(prettify(slug))} → ${esc(prettify(plan.species))}</b><small>${money(plan.coinsCost)} IdleCoin</small></div>
        <button type="button" class="master-btn" data-fossil="${slug}">Reviver</button></article>`).join('')}</div></section>`, { back: true });
}

function renderMine() {
  const s = state(); normalizeProfessions(s);
  openDialog('Mina', `Nível ${(s.professions?.mine?.level) || 1}`, `
    <button type="button" class="master-primary" data-mine-once="1">Minerar (${coin(13)} 50)</button>
    <p class="master-hint">Chance de scraps, nails, screws e essências.</p>`, { back: true });
}

function applyRewardBundle(s, reward) {
  if (!reward) return;
  const serverEco = !!hooks.serverEconomyReady?.();
  // Season/daily claims already credit wallet/inventory on the server —
  // don't double-apply coins/items locally (F5 refresh would fight the ledger).
  if (!serverEco) {
    if (reward.coins) s.stats.coins = (s.stats.coins || 0) + reward.coins;
    if (reward.emerald) s.emerald = (s.emerald || 0) + reward.emerald;
    if (reward.items) {
      s.inventory = s.inventory || {};
      for (const [slug, qty] of Object.entries(reward.items)) {
        if (qty > 0) s.inventory[slug] = (s.inventory[slug] || 0) + qty;
      }
    }
    if (reward.type === 'coins') s.stats.coins = (s.stats.coins || 0) + (reward.amount || 0);
    if (reward.type === 'emerald') s.emerald = (s.emerald || 0) + (reward.amount || 0);
    if (reward.type === 'item' && reward.slug) {
      s.inventory = s.inventory || {};
      s.inventory[reward.slug] = (s.inventory[reward.slug] || 0) + (reward.qty || 1);
    }
  }
  if (reward.type === 'boost' && BOOST_DEFS[reward.key]) {
    normalizeMonetization(s);
    s.monetization.boosts.pending.push({
      id: `pass-${reward.key}-${Date.now()}-${Math.floor(Math.random() * 1e6)}`,
      key: reward.key,
      hours: Math.max(1, Number(reward.hours) || 1),
      purchasedAt: Date.now(),
      source: 'season',
    });
  }
  if (reward.boost?.key && BOOST_DEFS[reward.boost.key]) {
    normalizeMonetization(s);
    s.monetization.boosts.pending.push({
      id: `daily-${reward.boost.key}-${Date.now()}-${Math.floor(Math.random() * 1e6)}`,
      key: reward.boost.key,
      hours: Math.max(1, Number(reward.boost.hours) || 1),
      purchasedAt: Date.now(),
      source: 'daily',
    });
  }
}

const QUEST_TYPE_LABELS = {
  defeat: 'Derrotar pokémons',
  capture: 'Capturar pokémons',
  craft: 'Craftar itens',
  fish: 'Pescar',
  sell: 'Vender itens',
};

function rewardVisualFromBundle(reward) {
  if (!reward) return '';
  if (reward.boost?.key) return boostIcon(reward.boost.key, 28);
  if (reward.emerald) return em(28);
  if (reward.items) {
    const [slug] = Object.entries(reward.items).sort((a, b) => b[1] - a[1])[0] || [];
    if (slug) return itemThumb(slug, 30);
  }
  if (reward.coins) return coin(28);
  return rewardVisual(reward);
}

function dailyRewardLabel(reward) {
  if (!reward) return '—';
  const parts = [];
  if (reward.coins) parts.push(`${money(reward.coins)} IdleCoin`);
  if (reward.emerald) parts.push(`${reward.emerald} Esmeralda${reward.emerald === 1 ? '' : 's'}`);
  if (reward.items) {
    for (const [slug, qty] of Object.entries(reward.items)) {
      parts.push(`${qty}× ${ITEM_BY_SLUG[slug]?.name || prettify(slug)}`);
    }
  }
  if (reward.boost?.key) {
    parts.push(`${reward.boost.hours || 1}h ${BOOST_DEFS[reward.boost.key]?.label || reward.boost.key}`);
  }
  return parts.join(' · ') || '—';
}

function applyDailyGiftSnapshot(s, response) {
  if (!response?.daily) return false;
  normalizeMeta(s);
  const lastClaimAt = Date.parse(response.daily.last_claim_at || '') || Number(response.daily.lastClaimAt) || 0;
  s.meta.daily.lastClaimAt = Math.max(0, lastClaimAt);
  s.meta.daily.streak = Math.max(0, Math.floor(Number(response.daily.streak) || 0));
  applyServerEconomySnapshot(response);
  return true;
}

async function syncDailyGiftFromServer(s) {
  if (!hooks.serverEconomyReady?.() || typeof hooks.fetchDailyGift !== 'function') return false;
  try {
    const response = await hooks.fetchDailyGift();
    const changed = applyDailyGiftSnapshot(s, response);
    if (changed) {
      hooks.save();
      hooks.refreshDailyGiftBadge?.();
    }
    return changed;
  } catch (error) {
    console.warn('fetch daily gift failed', error);
    return false;
  }
}

function renderDailyGift({ skipServerSync = false } = {}) {
  const s = state();
  normalizeMeta(s);
  const now = Date.now();
  const progress = dailyGiftProgress(s, now);
  const focusDay = progress.claimableDay || progress.claimedInCycle || 1;
  const focusReward = DAILY_REWARDS[focusDay - 1];
  openDialog('Daily Gift', `Ciclo de ${DAILY_GIFT_DAYS} dias · sequência ${progress.streak}`, `
    <div class="daily-gift-hero ${progress.canClaim ? 'ready' : 'done'}">
      <div class="daily-gift-hero-copy">
        <small class="daily-kicker">${progress.canClaim ? 'Presente disponível' : 'Volte amanhã'}</small>
        <h3>Dia <span>${focusDay}</span></h3>
        <p>${progress.canClaim
          ? 'Resgate o próximo presente disponível. A sequência fica salva mesmo se você passar dias sem entrar.'
          : `Você já resgatou o dia ${progress.claimedInCycle} deste ciclo. O próximo presente libera às 04:00 (horário de São Paulo).`}</p>
      </div>
      <div class="daily-gift-today">
        <span class="daily-gift-today-visual">${rewardVisualFromBundle(focusReward)}</span>
        <b>${esc(dailyRewardLabel(focusReward))}</b>
        <button type="button" class="master-primary" data-daily-claim="1" ${progress.canClaim ? '' : 'disabled'}>${progress.canClaim ? 'Resgatar presente' : 'Já resgatado'}</button>
      </div>
      <div class="daily-gift-stats">
        <span><small>Sequência</small><b>${progress.streak}</b></span>
        <span><small>Ciclo</small><b>${progress.claimedInCycle}<em>/${DAILY_GIFT_DAYS}</em></b></span>
        <span><small>Status</small><b class="${progress.canClaim ? 'on' : ''}">${progress.canClaim ? 'Pronto' : 'OK'}</b></span>
      </div>
    </div>
    <div class="daily-gift-grid" aria-label="Calendário de 60 dias">${DAILY_REWARDS.map((reward, index) => {
      const day = index + 1;
      const claimed = day <= progress.claimedInCycle;
      const today = day === progress.claimableDay;
      const milestone = day % 7 === 0 || day === 30 || day === 60;
      return `<article class="daily-day ${claimed ? 'claimed' : ''} ${today ? 'today' : ''} ${milestone ? 'milestone' : ''}" title="${esc(dailyRewardLabel(reward))}">
        <header><b>${day}</b>${claimed ? '<em>✓</em>' : today ? '<em>•</em>' : ''}</header>
        <span class="daily-day-visual">${rewardVisualFromBundle(reward)}</span>
        <small>${esc(dailyRewardLabel(reward))}</small>
      </article>`;
    }).join('')}</div>`, { eyebrow: 'Retenção' });
  if (!skipServerSync && hooks.serverEconomyReady?.()) {
    void syncDailyGiftFromServer(s).then((changed) => {
      if (changed) renderDailyGift({ skipServerSync: true });
    });
  }
}

function applyServerEconomySnapshot(response) {
  if (response?.wallet) hooks.acceptWallet?.(response.wallet);
  if (response?.inventory) hooks.applyInventory?.(response.inventory);
  if (response?.boosts) hooks.applyPremiumBoosts?.(response.boosts);
}

async function syncDailyQuestsFromServer(s) {
  if (!hooks.serverEconomyReady?.()) return false;
  try {
    const data = await hooks.fetchDailyQuests?.();
    if (!data?.quests || !Array.isArray(data.quests)) return false;
    normalizeMeta(s);
    const prev = s.meta.quests.active || [];
    s.meta.quests.active = data.quests.map((q) => {
      const local = prev.find((entry) => entry.id === q.quest_id);
      const target = Math.max(1, Number(q.target) || 1);
      const progress = data?.authoritative === false
        ? Math.max(0, Number(q.progress) || 0, Number(local?.progress) || 0)
        : Math.max(0, Number(q.progress) || 0);
      const claimed = !!q.claimed || !!local?.claimed;
      const completed = !!q.completed || !!local?.completed || progress >= target;
      return {
        id: q.quest_id,
        type: q.type,
        category: q.category,
        target,
        progress: Math.min(target, progress),
        completed,
        claimed,
      };
    });
    s.meta.quests.dailyBonusClaimed = !!data.bonus_claimed || !!s.meta.quests.dailyBonusClaimed;
    return true;
  } catch (error) {
    console.warn('fetch daily quests failed', error);
    return false;
  }
}

function renderMeta({ skipServerSync = false } = {}) {
  const s = state(); normalizeMeta(s);
  if (!hooks.serverEconomyReady?.() && questsNeedRotation(s)) { rotateQuests(s); hooks.save(); }
  checkAchievements(s);
  const log = s.meta.collectionLog || {};
  const quests = s.meta.quests?.active || [];
  const unlocked = new Set(s.meta.achievements?.unlocked || []);
  openDialog('Missões & Coleção', `Streak ${s.meta?.daily?.streak || 0} · Pass ${s.meta?.passPoints || 0} pts`, `
    <section class="master-section"><small class="eyebrow">Missões do dia</small>
    <div class="master-list">${quests.map((q) => {
      const tpl = QUEST_TEMPLATES.find((row) => row.id === q.id);
      const pct = Math.min(100, Math.round(((q.progress || 0) / (q.target || 1)) * 100));
      const stateLabel = !RELEASE_FEATURES.dailyQuestRewards ? 'Em preparação' : q.claimed ? 'Resgatada' : q.completed ? 'Resgatar' : `${q.progress || 0}/${q.target || 0}`;
      return `<article class="master-row ${q.completed && !q.claimed ? 'ready' : ''}"><div><b>${esc(QUEST_TYPE_LABELS[tpl?.type || q.type] || prettify(tpl?.type || q.type || q.id))}</b><small>${q.progress || 0}/${q.target || 0}${q.claimed ? ' · resgatada' : q.completed ? ' · pronta' : ''}</small><i class="master-bar"><u style="width:${q.claimed ? 100 : pct}%"></u></i></div>
        <button type="button" class="master-btn" data-claim-quest="${q.id}" ${RELEASE_FEATURES.dailyQuestRewards && q.completed && !q.claimed ? '' : 'disabled'}>${stateLabel}</button></article>`;
    }).join('') || '<p class="master-hint">Sem missões</p>'}</div></section>
    <section class="master-section"><small class="eyebrow">Collection Log</small>
    <div class="master-perk-row"><span>✦ ${(log.shinies || []).length}</span><span>IV ${(log.ivQuality || []).length}</span><span>🎣 ${(log.fish || []).length}</span><span>⚒ ${(log.crafts || []).length}</span><span>🦴 ${(log.fossils || []).length}</span></div></section>
    <section class="master-section"><small class="eyebrow">Conquistas</small>
    <div class="master-list">${ACHIEVEMENTS.map((a) => `<article class="master-row ${unlocked.has(a.id) ? 'done' : ''}"><div><b>${esc(a.name || a.id)}</b><small>${coin(12)} ${money(a.rewardCoins)}</small></div><span class="master-check">${unlocked.has(a.id) ? '✓' : '…'}</span></article>`).join('')}</div></section>`, { back: true });
  if (!skipServerSync && hooks.serverEconomyReady?.()) {
    void syncDailyQuestsFromServer(s).then((ok) => {
      if (ok) { hooks.save(); renderMeta({ skipServerSync: true }); }
    });
  }
}

function emeraldShopTabsHtml() {
  const tabs = [
    ['packages', 'Pacotes', em(14)],
    ['boosts', 'Boosts', uiIcon('sparkles')],
    ['vip', 'VIP', uiIcon('crown')],
  ];
  return `<nav class="emerald-shop-tabs" aria-label="Seções da loja">${tabs.map(([id, label, icon]) => `
    <button type="button" class="emerald-shop-tab ${emeraldShopTab === id ? 'active' : ''}" data-emerald-tab="${id}">
      <span class="emerald-shop-tab-icon">${icon}</span><b>${label}</b>
    </button>`).join('')}</nav>`;
}

function renderEmeraldPackages(s) {
  const pile = (pkg) => `<span class="emerald-pile emerald-pile-${pkg.id}" aria-hidden="true">${Array.from(
    { length: pkg.pileSize },
    (_, index) => `<i style="--gem:${index};--tilt:${((index % 7) - 3) * 3}deg">${em(index % 5 === 0 ? 22 : 18)}</i>`,
  ).join('')}</span>`;
  return `
    <div class="emerald-store-balance"><span>${em(24)}<span><small>Seu saldo</small><b>${money(s.emerald || 0)} Esmeraldas</b></span></span><small>Pagamento seguro processado pela Stripe</small></div>
    <section class="master-section emerald-store-section">
      <div class="emerald-store-heading"><div><small class="eyebrow">Pacotes recomendados</small><h3>Escolha seu pacote</h3></div><span>Quanto maior o pacote, menor o valor unitário.</span></div>
      <div class="emerald-package-grid">${EMERALD_PACKAGES.map((p) => `
        <article class="emerald-package-card ${p.featured ? 'featured' : ''}">
          ${p.featured ? '<span class="emerald-package-badge">Mais escolhido</span>' : ''}
          ${pile(p)}
          <div><b>${p.emerald} Esmeraldas</b><small>${esc(p.label)}</small></div>
          <strong>${formatBrlFromCents(p.priceCents)}</strong>
          <em>${formatBrlFromCents(p.priceCents / p.emerald)} por Esmeralda</em>
          <button type="button" class="master-primary" data-emerald-checkout="${p.emerald}" ${emeraldCheckoutBusy ? 'disabled' : ''}>Adquirir pacote</button>
        </article>`).join('')}</div>
    </section>
    <label class="emerald-purchase-consent">
      <input type="checkbox" id="emeraldTermsConsent" ${emeraldTermsAccepted ? 'checked' : ''}>
      <span>Entendo que estou adquirindo uma moeda virtual de uso exclusivo dentro do IdleMon, sem valor de saque, rendimento ou conversão em dinheiro. A receita ajuda a manter e desenvolver o projeto. O pagamento não é uma doação.</span>
    </label>
    <p class="emerald-purchase-note">O crédito é liberado somente após a confirmação do pagamento pela Stripe. Não informe dados de cartão dentro do jogo: o pagamento sempre abre na página segura da Stripe.</p>
    <section class="master-section"><small class="eyebrow">${esc(t('shop.emeraldBalls') || 'Bolas premium')}</small>
    <div class="master-list">${EMERALD_BALL_OFFERS.map((o) => `
      <article class="master-row emerald-ball-row"><div class="emerald-ball-copy">${itemThumb(o.slug, 34)}<div><b>${esc(o.label)}</b><small>${o.qty}× · ${o.cost} ${em(13)}</small></div></div>
        <button type="button" class="master-btn" data-buy-emerald-ball="${o.id}">Comprar</button></article>`).join('')}</div></section>`;
}

function renderEmeraldBoosts(s) {
  const now = Date.now();
  const pending = s.monetization?.boosts?.pending || [];
  const owned = pending.reduce((map, entry) => {
    const row = map[entry.key] || { qty: 0, hours: 0 };
    row.qty += 1;
    row.hours += Math.max(1, Number(entry.hours) || 1);
    map[entry.key] = row;
    return map;
  }, {});
  const shopBoosts = Object.values(BOOST_DEFS).filter((b) => BOOST_PRODUCT_IDS[b.key]);
  const inventoryKeys = shopBoosts.filter((b) => owned[b.key]?.qty);
  const inventoryRows = inventoryKeys.length
    ? `<div class="boost-inventory-grid">${inventoryKeys.map((b) => {
      const { qty, hours } = owned[b.key];
      const active = activeBoostMult(s, b.key, now) > 1;
      const expires = s.monetization.boosts.active?.[b.key]?.expiresAt;
      return `<article class="boost-inv-card ${active ? 'active' : ''}" style="--boost-accent:${BOOST_ACCENTS[b.key]}">
        ${boostIcon(b.key, 32)}
        <div><b>${esc(b.label)}</b><small>×${b.mult} · ${hours}h guardada${hours === 1 ? '' : 's'}</small>
          <em class="boost-inv-qty">×${qty} no inventário</em>
          ${active ? `<em class="boost-inv-timer">Ativo · ${formatBoostRemaining(expires, now)}</em>` : ''}
        </div>
        <button type="button" class="master-btn" data-activate-boost="${b.key}" ${active || qty <= 0 ? 'disabled' : ''}>Ativar</button>
      </article>`;
    }).join('')}</div>`
    : '<p class="master-hint">Compre boosts de 1h — eles vão para o inventário e você ativa quando quiser.</p>';

  return `
    <div class="emerald-store-balance"><span>${em(24)}<span><small>Seu saldo</small><b>${money(s.emerald || 0)} Esmeraldas</b></span></span><small>Boosts de 1 hora · ativação sob demanda</small></div>
    <section class="master-section">
      <div class="emerald-store-heading"><div><small class="eyebrow">Inventário</small><h3>Seus boosts prontos</h3></div><span>${pending.length} item${pending.length === 1 ? '' : 's'}</span></div>
      ${inventoryRows}
    </section>
    <section class="master-section">
      <div class="emerald-store-heading"><div><small class="eyebrow">Loja</small><h3>Comprar boost (1h)</h3></div><span>Vai direto para o inventário</span></div>
      <div class="boost-shop-grid">${shopBoosts.map((b) => {
        const active = activeBoostMult(s, b.key, now) > 1;
        const qty = owned[b.key]?.qty || 0;
        const price = boostPrice(b.key, 1);
        const bonusPct = Math.round((b.mult - 1) * 100);
        return `<article class="boost-shop-card ${active ? 'ready' : ''}" style="--boost-accent:${BOOST_ACCENTS[b.key]}">
          <header>${boostIcon(b.key, 36)}<span class="boost-mult-badge">+${bonusPct}%</span></header>
          <b>${esc(b.label)}</b>
          <small>×${b.mult} por 1 hora</small>
          <em class="boost-owned">${qty > 0 ? `${qty} no inventário` : 'Sem estoque'}</em>
          <strong>${price} ${em(14)}</strong>
          <button type="button" class="master-btn" data-buy-boost="${b.key}">Comprar 1h</button>
        </article>`;
      }).join('')}</div>
    </section>`;
}

function renderEmeraldVip(s) {
  const now = Date.now();
  const active = isVip(s, now);
  const perks = vipPerks();
  const expiresAt = s.monetization?.vip?.expiresAt || 0;
  const daysLeft = active ? Math.max(1, Math.ceil((expiresAt - now) / 86400000)) : 0;
  const xpPct = Math.round((perks.trainerXpMultiplier - 1) * 100);
  const shinyPct = Math.round((perks.shinyMultiplier - 1) * 100);
  const feePct = Math.round(perks.marketFeeRate * 100);
  return `
    <div class="vip-hero ${active ? 'is-active' : ''}">
      <div class="vip-hero-badge">${uiIcon('crown')}</div>
      <div class="vip-hero-copy">
        <small class="vip-kicker">${active ? 'Assinatura ativa' : 'Assinatura premium'}</small>
        <h3>Clube <span>VIP</span></h3>
        <p>${active ? `Faltam <b>${daysLeft} dia${daysLeft === 1 ? '' : 's'}</b> · renove para manter os bônus` : 'Conveniência, cosmético e qualidade de vida — sem poder bruto no Market.'}</p>
      </div>
      ${active ? `<span class="vip-status-pill">♛ Ativo</span>` : ''}
    </div>
    <div class="vip-nametag-preview" aria-label="Prévia da nametag VIP">
      <span class="chat-vip-tag">♛ VIP</span>
      <strong>SeuNome</strong>
      <em>assim você aparece no chat</em>
    </div>
    <div class="vip-perk-grid">
      <article class="vip-perk" style="--vip-tone:#f1c40f"><b>+${xpPct}%</b><small>XP Treinador</small></article>
      <article class="vip-perk" style="--vip-tone:#5dade2"><b>+${xpPct}%</b><small>XP Pokémon</small></article>
      <article class="vip-perk" style="--vip-tone:#c68cff"><b>+${shinyPct}%</b><small>Chance Shiny</small></article>
      <article class="vip-perk" style="--vip-tone:#6ed497"><b>${feePct}%</b><small>Taxa do Market</small></article>
      <article class="vip-perk" style="--vip-tone:#ff8268"><b>+${perks.offlineCapBonusHours}h</b><small>Teto offline</small></article>
      <article class="vip-perk vip-perk-wide" style="--vip-tone:#ffe27a"><b>♛ Nametag</b><small>Selo dourado no chat</small></article>
      <article class="vip-perk" style="--vip-tone:#81b8ff"><b>Auto</b><small>Restock bolas comuns</small></article>
      <article class="vip-perk" style="--vip-tone:#e5ae65"><b>+1</b><small>Slot Market & Box</small></article>
    </div>
    <section class="master-section">
      <div class="emerald-store-heading"><div><small class="eyebrow">Planos</small><h3>Escolha a duração</h3></div><span>Saldo: ${em(14)} ${money(s.emerald || 0)}</span></div>
      <div class="vip-plan-grid">${VIP_PLANS.map((p, index) => `
        <article class="vip-plan-card ${index === 1 ? 'featured' : ''}">
          ${index === 1 ? '<span class="vip-plan-badge">Melhor valor</span>' : ''}
          <b>${p.days}</b><small>dias</small>
          <strong>${p.cost} ${em(16)}</strong>
          <em>${(p.cost / p.days).toFixed(2)} ${em(12)}/dia</em>
          <button type="button" class="master-primary" data-buy-vip="${p.days}">${active ? 'Estender' : 'Assinar'}</button>
        </article>`).join('')}</div>
    </section>`;
}

function renderEmeraldShop() {
  const s = state(); normalizeMonetization(s);
  if (!['packages', 'boosts', 'vip'].includes(emeraldShopTab)) emeraldShopTab = 'packages';
  const titles = {
    packages: ['Loja de Esmeraldas', 'Apoie o desenvolvimento e receba moeda virtual no jogo'],
    boosts: ['Boosts', 'Compre 1h · guarde no inventário · ative quando quiser'],
    vip: ['Clube VIP', 'Bônus de conveniência com estilo'],
  };
  const [title, sub] = titles[emeraldShopTab];
  const body = emeraldShopTab === 'boosts' ? renderEmeraldBoosts(s)
    : emeraldShopTab === 'vip' ? renderEmeraldVip(s)
      : renderEmeraldPackages(s);
  openDialog(title, sub, `${emeraldShopTabsHtml()}${body}`, { eyebrow: 'Esmeraldas' });
}

function renderPass() {
  const s = state(); normalizeMonetization(s); normalizeMeta(s);
  const points = s.meta?.passPoints || 0;
  const currentTier = SEASON_TIERS.reduce((best, tier) => (points >= tier.pointsRequired ? tier.tier : best), 0);
  const rewardLabel = (reward) => {
    if (reward.type === 'coins') return `${money(reward.amount)} IdleCoin`;
    if (reward.type === 'emerald') return `${reward.amount} Esmeralda${reward.amount === 1 ? '' : 's'}`;
    if (reward.type === 'boost') return `${reward.hours}h ${BOOST_DEFS[reward.key]?.label || reward.key}`;
    return `${reward.qty || 1}× ${ITEM_BY_SLUG[reward.slug]?.name || prettify(reward.slug)}`;
  };
  openDialog('Game Pass', `Temporada 1 · ${points} pts · nível ${currentTier}/50`, `
    <div class="season-pass-hero">
      <div class="season-pass-hero-copy">
        <small class="season-kicker">Temporada em andamento</small>
        <h3>Game <span>Pass</span></h3>
        <p>50 níveis com trilha grátis e premium. Missões, ginásios e raids geram pontos.</p>
      </div>
      <div class="season-pass-stats">
        <span><small>Pontos</small><b>${money(points)}</b></span>
        <span><small>Nível</small><b>${currentTier}<em>/50</em></b></span>
        <span><small>Premium</small><b class="${s.monetization.season.premium ? 'on' : ''}">${s.monetization.season.premium ? 'Ativo' : 'Off'}</b></span>
      </div>
      <button type="button" class="master-primary season-pass-buy" data-buy-pass="1" ${s.monetization.season.premium ? 'disabled' : ''}>${s.monetization.season.premium ? 'Premium desbloqueado' : `Desbloquear Premium · ${SEASON_PREMIUM_COST} ${em(14)}`}</button>
    </div>
    <div class="season-track" aria-label="Trilha de recompensas da temporada"><div class="season-tier-grid">${SEASON_TIERS.map((tier) => {
      const unlocked = points >= tier.pointsRequired;
      const freeClaimed = s.monetization.season.claimed.free.includes(tier.tier);
      const premiumClaimed = s.monetization.season.claimed.premium.includes(tier.tier);
      return `<article class="season-tier ${unlocked ? 'unlocked' : ''} ${tier.tier === currentTier ? 'current' : ''}">
        <header><b>Nv. ${tier.tier}</b><small>${tier.pointsRequired} pts</small></header>
        <button type="button" data-claim-pass="${tier.tier}" data-pass-track="free" ${unlocked && !freeClaimed ? '' : 'disabled'}>
          <span class="season-reward-visual">${rewardVisual(tier.free)}</span>
          <small>Grátis</small><b>${esc(rewardLabel(tier.free))}</b>
          <em>${freeClaimed ? 'Resgatado' : unlocked ? 'Resgatar' : 'Bloqueado'}</em>
        </button>
        <button type="button" class="premium" data-claim-pass="${tier.tier}" data-pass-track="premium" ${unlocked && s.monetization.season.premium && !premiumClaimed ? '' : 'disabled'}>
          <span class="season-reward-visual">${rewardVisual(tier.premium)}</span>
          <small>Premium</small><b>${esc(rewardLabel(tier.premium))}</b>
          <em>${premiumClaimed ? 'Resgatado' : unlocked && s.monetization.season.premium ? 'Resgatar' : 'Bloqueado'}</em>
        </button>
      </article>`;
    }).join('')}</div></div>`, { eyebrow: 'Temporada' });
}

function renderGym() {
  const s = state();
  s.badges = Array.isArray(s.badges) ? s.badges : [];
  s.eliteProgress = s.eliteProgress && typeof s.eliteProgress === 'object' ? s.eliteProgress : { defeated: [], championDefeated: false };
  const power = hooks.teamPower();
  openDialog('Ginásios · Elite · Campeão', `Poder ~ ${money(power)}`, `
    <div class="master-list">${GYMS.map((g) => {
      const unlocked = canChallengeGym(s, g.id);
      const won = s.badges.includes(g.badgeId);
      return `<article class="master-row ${won ? 'done' : ''}"><div><b>${esc(g.leaderName)}</b><small>${esc(g.city)} · ${esc(g.type)} · ${money(g.recommendedPower)}</small></div>
        <span class="master-check">${won ? '🏅' : ''}</span>
        <button type="button" class="master-btn" data-gym="${g.id}" ${unlocked ? '' : 'disabled'}>Desafiar</button></article>`;
    }).join('')}</div>
    <section class="master-section"><small class="eyebrow">Elite Four</small>
    <div class="master-list">${ELITE_FOUR.map((g) => {
      const unlocked = canChallengeElite(s, g.id);
      const won = (s.eliteProgress.defeated || []).includes(g.id);
      return `<article class="master-row ${won ? 'done' : ''}"><div><b>${esc(g.leaderName)}</b><small>${esc(g.type)} · ${money(g.recommendedPower)}</small></div>
        <span class="master-check">${won ? '✓' : ''}</span>
        <button type="button" class="master-btn" data-elite="${g.id}" ${unlocked ? '' : 'disabled'}>Desafiar</button></article>`;
    }).join('')}</div></section>
    <section class="master-section"><small class="eyebrow">Campeão</small>
    <article class="master-row ${s.eliteProgress.championDefeated ? 'done' : ''}"><div><b>${esc(CHAMPION.leaderName)}</b><small>${money(CHAMPION.recommendedPower)}</small></div>
      <span class="master-check">${s.eliteProgress.championDefeated ? '👑' : ''}</span>
      <button type="button" class="master-btn" data-champion="1" ${canChallengeChampion(s) ? '' : 'disabled'}>Desafiar</button></article></section>`, { back: true });
}

function refreshClanFromServer() {
  if (remoteClanLoaded || remoteClanLoading) return;
  remoteClanLoading = true;
  hooks.fetchClan?.().then((data) => {
    remoteClan = data?.clan || null;
    remoteClanLoaded = true;
    remoteClanLoading = false;
    renderClan();
  }).catch((error) => {
    remoteClanLoading = false;
    console.warn('clan sync failed', error);
  });
}

function renderClan() {
  refreshClanFromServer();
  const clan = remoteClan;
  openDialog('Clãs', clan ? `Rank ${clan.rank}` : remoteClanLoaded ? 'Sem clã' : 'Carregando estado do servidor…', `
    ${remoteClanLoaded && !clan ? `<button type="button" class="master-primary" data-join-clan="saffron-clan">Entrar no Clã Saffron (Nv.80+)</button>` : ''}
    <div class="master-list">${CLAN_RANKS.map((r) => `
      <article class="master-row ${(clan?.rank || 0) >= r.rank ? 'done' : ''}"><div><b>${esc(r.name)}</b><small>${r.skipCost ? `${money(r.skipCost)} IdleCoin` : '—'}</small></div>
        <button type="button" class="master-btn" data-clan-rank="${r.rank}" ${clan && (clan.rank || 0) < r.rank ? '' : 'disabled'}>Subir</button></article>`).join('')}</div>
    <button type="button" class="master-btn" data-open-panel="raid">Raid Board</button>`, { back: true });
}

function formatRaidRemaining(raid, now = Date.now()) {
  const endsAt = Date.parse(raid?.ends_at || '') || 0;
  const ms = Math.max(0, endsAt - now);
  const hours = Math.floor(ms / 3600000);
  const mins = Math.floor((ms % 3600000) / 60000);
  if (hours > 0) return t('raid.timeHours', { hours, mins });
  return t('raid.timeMins', { mins });
}

function renderRaid() {
  if (!remoteRaidLoaded) {
    remoteRaidLoaded = true;
    hooks.fetchRaid?.().then((data) => { remoteRaid = data || null; renderRaid(); }).catch((error) => {
      remoteRaidLoaded = false;
      console.warn('raid sync failed', error);
    });
  }
  const raid = remoteRaid;
  if (!raid) {
    openDialog(t('raid.title'), 'Carregando estado do servidor…', '<p class="master-hint">A Raid será exibida assim que o servidor responder.</p>', { back: true });
    return;
  }
  const now = Date.now();
  const contrib = Math.max(0, Math.floor(Number(raid.contribution) || 0));
  const claimed = !!raid.claimed;
  const defeated = raid.status === 'defeated';
  const expired = raid.status === 'expired';
  const active = raid.status === 'active';
  const claimable = !active && contrib > 0 && !claimed;
  const dealtPct = Math.max(0, Math.min(100, Math.round((1 - Number(raid.hp) / Math.max(1, Number(raid.total_hp))) * 100)));
  const statusText = claimed ? t('raid.statusClaimed')
    : defeated ? t('raid.statusDefeated')
    : expired ? t('raid.statusExpired')
    : t('raid.statusActive', { time: formatRaidRemaining(raid, now) });
  const claimLabel = claimed ? t('raid.claimed') : t('raid.claim');
  const claimHint = claimable ? t('raid.claimHintReady')
    : claimed ? t('raid.claimHintDone')
    : contrib <= 0 ? t('raid.claimHintNoDmg')
    : active ? t('raid.claimHintActive')
    : t('raid.claimHintWait');
  openDialog(t('raid.title'), `${prettify(raid.species)} · HP ${money(raid.hp)}/${money(raid.total_hp)}`, `
    <p class="master-hint">${esc(t('raid.hint'))}</p>
    <div class="master-row"><div>
      <b>${esc(prettify(raid.species))}</b>
      <small>${esc(statusText)}</small>
      <small>${esc(t('raid.yourContrib', { value: money(contrib) }))} · ${esc(t('raid.damagePct', { pct: dealtPct }))}</small>
      <i class="master-bar danger"><u style="width:${dealtPct}%"></u></i>
    </div></div>
    <button type="button" class="master-primary" data-raid-claim="1" ${claimable ? '' : 'disabled'}>${esc(claimLabel)}</button>
    <p class="master-hint">${esc(claimHint)}</p>`, { back: true });
}

function wireBody() {
  const body = document.getElementById('masterSystemsBody');
  if (!body) return;
  body.onchange = (event) => {
    if (event.target?.id === 'emeraldTermsConsent') emeraldTermsAccepted = event.target.checked;
  };
  body.onclick = async (e) => {
  const el = e.target.closest('[data-open-panel],[data-emerald-tab],[data-buy-upgrade],[data-craft],[data-fossil],[data-daily-claim],[data-claim-quest],[data-emerald-checkout],[data-emerald-step],[data-buy-emerald-ball],[data-buy-boost],[data-activate-boost],[data-buy-vip],[data-buy-pass],[data-claim-pass],[data-gym],[data-elite],[data-champion],[data-clan-rank],[data-join-clan],[data-raid-claim],[data-hunt-diff]');
    if (!el) return;
    const s = state();
    if (el.dataset.huntDiff != null) {
      s.huntDifficulty = Number(el.dataset.huntDiff) || 0;
      hooks.save(); hooks.showToast(`Dificuldade +${s.huntDifficulty}`); renderHub();
      return;
    }
    if (el.dataset.emeraldTab) {
      emeraldShopTab = el.dataset.emeraldTab;
      renderEmeraldShop();
      return;
    }
    if (el.dataset.openPanel) { openPanel(el.dataset.openPanel); return; }
    if (el.dataset.buyUpgrade) {
      const res = applyBuy(s, el.dataset.buyUpgrade);
      hooks.showToast(res.ok ? 'Upgrade ok' : (res.error || 'Falhou'));
      if (res.ok) { hooks.save(); hooks.refreshEconomy(); renderUpgrades(); }
      return;
    }
    if (el.dataset.craft) {
      const res = craftItem(s, el.dataset.craft);
      if (res.ok) {
        try {
          progressQuest(s, 'craft', 1);
          logCraftCompleted(s, el.dataset.craft);
          checkAchievements(s);
        } catch {}
      }
      hooks.showToast(res.ok ? `+${res.produced.qty} ${res.produced.slug}` : (res.error || 'Falhou'));
      if (res.ok) { hooks.save(); hooks.refreshEconomy(); renderCraft(); }
      return;
    }
    if (el.dataset.fossil) {
      const res = reviveFossil(s, el.dataset.fossil);
      if (res.ok) {
        try { logFossilRevival(s, el.dataset.fossil); checkAchievements(s); } catch {}
      }
      hooks.showToast(res.ok ? `Reviveu ${res.species}` : (res.error || 'Falhou'));
      if (res.ok) { hooks.save(); hooks.refreshEconomy(); }
      return;
    }
    if (el.dataset.dailyClaim) {
      el.disabled = true;
      try {
        let res;
        if (hooks.serverEconomyReady?.() && typeof hooks.claimDailyGift === 'function') {
          res = await hooks.claimDailyGift();
          if (!applyDailyGiftSnapshot(s, res)) throw new Error('daily_gift_invalid_response');
          hooks.trackEvent('daily_claimed', { streak: res.daily?.streak || 0, day: res.daily?.day || 0, source: 'server' });
        } else {
          res = claimDailyGift(s, Date.now());
          if (res.ok) applyRewardBundle(s, res.reward);
          if (res.ok) hooks.trackEvent('daily_claimed', { streak: res.streak, day: res.day, source: 'local' });
        }
        hooks.showToast(res.ok === false ? (res.error || 'Já resgatado') : `Dia ${res.daily?.day || res.day} · streak ${res.daily?.streak || res.streak}`);
        if (res.ok !== false) { hooks.save(); hooks.refreshEconomy(); }
      } catch (error) {
        const code = String(error?.message || error || '');
        hooks.showToast(code.includes('already_claimed') ? 'Já resgatado hoje' : 'Não foi possível resgatar o Daily Gift');
      }
      renderDailyGift({ skipServerSync: true });
      hooks.refreshDailyGiftBadge?.();
      return;
    }
    if (el.dataset.claimQuest) {
      if (!RELEASE_FEATURES.dailyQuestRewards) {
        hooks.showToast(unavailableFeatureMessage('dailyQuestRewards'));
        return;
      }
      const questId = el.dataset.claimQuest;
      el.disabled = true;
      try {
        if (hooks.serverEconomyReady?.()) {
          await syncDailyQuestsFromServer(s);
          const quest = (s.meta.quests?.active || []).find((entry) => entry.id === questId);
          if (!quest) throw new Error('quest_not_found');
          if (!quest.completed) throw new Error('quest_not_completed');
          if (quest.claimed) throw new Error('already_claimed');
          const claim = await hooks.claimDailyQuest(questId);
          quest.claimed = true;
          grantPassPoints(s, claim?.reward_pass_points || 15, 'quest');
          applyServerEconomySnapshot(claim);
          hooks.trackEvent('quest_claimed', {
            quest_id: questId,
            pass_points: claim?.reward_pass_points || 0,
            completion_bonus: claim?.completion_bonus || 0,
          });
          hooks.showToast(`+${claim?.reward_pass_points || 0} Pass XP`);
          hooks.save(); hooks.refreshEconomy(); renderMeta({ skipServerSync: true });
        } else {
          const res = claimQuest(s, questId);
          if (res.ok) {
            grantPassPoints(s, res.rewardPassPoints || 0, 'quest');
            hooks.trackEvent('quest_claimed', { quest_id: questId, pass_points: res.rewardPassPoints || 0, completion_bonus: res.completionBonus || 0 });
          }
          hooks.showToast(res.ok ? `+${res.rewardPassPoints || 0} Pass XP` : (res.error || 'Falhou'));
          if (res.ok) { hooks.save(); hooks.refreshEconomy(); renderMeta({ skipServerSync: true }); }
          else el.disabled = false;
        }
      } catch (error) {
        const code = String(error?.message || error || '');
        hooks.showToast(
          code.includes('already_claimed') ? 'Já resgatada'
            : code.includes('quest_not_completed') ? 'Missão incompleta'
              : code.includes('quest_not_found') ? 'Missão não encontrada'
                : 'Não foi possível resgatar',
        );
        el.disabled = false;
        renderMeta({ skipServerSync: true });
      }
      return;
    }
    if (el.dataset.emeraldCheckout != null) {
      if (!RELEASE_FEATURES.paidEmeralds) {
        hooks.showToast(unavailableFeatureMessage('paidEmeralds'));
        return;
      }
      if (emeraldCheckoutBusy) return;
      if (!emeraldTermsAccepted) {
        hooks.showToast('Confirme os termos da aquisição para continuar.');
        body.querySelector('#emeraldTermsConsent')?.focus();
        return;
      }
      const quote = quoteEmeraldPurchase(Number(el.dataset.emeraldCheckout));
      if (!quote.ok) {
        hooks.showToast('Escolha uma quantidade válida de Esmeraldas.');
        return;
      }
      emeraldCheckoutBusy = true;
      hooks.trackEvent('checkout_started', { emerald_amount: quote.amount, total_cents: quote.totalCents });
      body.querySelectorAll('[data-emerald-checkout]').forEach((button) => { button.disabled = true; });
      hooks.showToast('Abrindo o pagamento seguro da Stripe…');
      try {
        await hooks.beginEmeraldCheckout(quote.amount);
      } catch (error) {
        console.error('emerald checkout failed', error);
        emeraldCheckoutBusy = false;
        const code = String(error?.message || error || '');
        hooks.showToast(
          code.includes('rate_limit')
            ? 'Muitas tentativas. Aguarde um minuto.'
            : code.includes('test_mode') || code.includes('disabled')
              ? 'Pagamentos em homologação. Nenhuma cobrança real está habilitada.'
              : 'Não foi possível abrir o pagamento agora.',
        );
        renderEmeraldShop();
      }
      return;
    }
    if (el.dataset.buyEmeraldBall) {
      const offer = EMERALD_BALL_OFFERS.find((entry) => entry.id === el.dataset.buyEmeraldBall);
      if (!offer) return;
      el.disabled = true;
      try {
        const purchase = await hooks.purchasePremiumProduct(offer.id);
        s.emerald = Number(purchase?.wallet?.emerald) || 0;
        const res = buyEmeraldBall(s, offer.id, { serverDebited: true });
        if (res.ok) hooks.trackEvent('premium_purchase', { product: 'ball', offer_id: offer.id, emerald_cost: offer.cost });
        hooks.showToast(res.ok ? `+${res.qty} ${res.slug}` : 'Falhou');
        if (res.ok) { hooks.save(); hooks.refreshEconomy?.(); renderEmeraldShop(); }
      } catch (error) {
        hooks.showToast(String(error?.message || '').includes('insufficient_emerald') ? 'Sem Esmeraldas suficientes.' : 'Não foi possível concluir a compra.');
        renderEmeraldShop();
      }
      return;
    }
    if (el.dataset.buyBoost) {
      const key = el.dataset.buyBoost;
      const hours = 1;
      const productId = BOOST_PRODUCT_IDS[key];
      if (!productId) return;
      el.disabled = true;
      try {
        const purchase = await hooks.purchasePremiumProduct(productId, hours);
        s.emerald = Number(purchase?.wallet?.emerald) || 0;
        const res = buyBoost(s, key, hours, Date.now(), { serverDebited: true });
        if (res.ok && purchase?.boost_id) {
          const pending = s.monetization.boosts.pending;
          const last = pending[pending.length - 1];
          if (last) last.id = purchase.boost_id;
        }
        if (res.ok) hooks.trackEvent('premium_purchase', { product: 'boost', boost: key, hours, emerald_cost: purchase?.spent });
        hooks.showToast(res.ok ? 'Boost no inventário' : (res.error || 'Falhou'));
        if (res.ok) { hooks.save(); emeraldShopTab = 'boosts'; renderEmeraldShop(); }
      } catch (error) {
        const code = String(error?.message || '');
        hooks.showToast(
          code.includes('insufficient_emerald') ? 'Sem Esmeraldas suficientes.'
            : code.includes('daily_limit') ? 'Limite diário deste boost.'
              : 'Não foi possível concluir a compra.',
        );
        emeraldShopTab = 'boosts';
        renderEmeraldShop();
      }
      return;
    }
    if (el.dataset.activateBoost) {
      const key = el.dataset.activateBoost;
      normalizeMonetization(s);
      const pending = s.monetization.boosts.pending.find((entry) => entry.key === key);
      el.disabled = true;
      try {
        if (pending?.id && String(pending.id).includes('-') && pending.id.length > 20) {
          await hooks.activatePremiumBoost(pending.id);
        }
        const res = activateBoost(s, key, Date.now());
        hooks.showToast(res.ok ? 'Boost ativo' : (res.error || 'Nada'));
        if (res.ok) { hooks.save(); emeraldShopTab = 'boosts'; renderEmeraldShop(); }
      } catch (error) {
        hooks.showToast(String(error?.message || '').includes('already_active') ? 'Já há boost ativo deste tipo.' : 'Não foi possível ativar.');
        emeraldShopTab = 'boosts';
        renderEmeraldShop();
      }
      return;
    }
    if (el.dataset.buyVip) {
      const plan = VIP_PLANS.find((entry) => entry.days === Number(el.dataset.buyVip));
      if (!plan) return;
      el.disabled = true;
      try {
        const purchase = await hooks.purchaseVip(plan.days);
        s.emerald = Number(purchase?.wallet?.emerald) || 0;
        applyVipEntitlement(s, purchase?.vip_expires_at);
      } catch (error) {
        hooks.showToast(String(error?.message || '').includes('insufficient_emerald') ? 'Sem Esmeraldas suficientes.' : 'Não foi possível concluir a compra.');
        emeraldShopTab = 'vip';
        renderEmeraldShop();
        return;
      }
      hooks.trackEvent('premium_purchase', { product: 'vip', days: plan.days, emerald_cost: plan.cost });
      hooks.showToast('VIP ativo');
      hooks.save(); hooks.refreshEconomy?.(); emeraldShopTab = 'vip'; renderEmeraldShop();
      return;
    }
    if (el.dataset.buyPass) {
      el.disabled = true;
      try {
        const purchase = await hooks.purchaseSeasonPass(s.monetization.season.id || 'season-1');
        s.emerald = Number(purchase?.wallet?.emerald) || 0;
        const res = buyPremiumPass(s, { serverDebited: true });
        if (res.ok) hooks.trackEvent('premium_purchase', { product: 'pass', season: s.monetization.season.id, emerald_cost: SEASON_PREMIUM_COST });
        hooks.showToast(res.ok ? 'Pass Premium' : (res.error || 'Falhou'));
        if (res.ok) { hooks.save(); renderPass(); }
      } catch (error) {
        const code = String(error?.message || '');
        hooks.showToast(
          code.includes('insufficient_emerald') ? 'Sem Esmeraldas suficientes.'
            : code.includes('already_premium') ? 'Pass já desbloqueado.'
              : 'Não foi possível concluir a compra.',
        );
        renderPass();
      }
      return;
    }
    if (el.dataset.claimPass) {
      normalizeMeta(s);
      const tier = Number(el.dataset.claimPass);
      const track = el.dataset.passTrack === 'premium' ? 'premium' : 'free';
      el.disabled = true;
      try {
        const claim = await hooks.claimSeasonTier(s.monetization.season.id || 'season-1', tier, track);
        const res = claimTier(s, tier, track);
        if (res.ok) applyRewardBundle(s, claim?.reward || res.reward);
        if (res.ok) hooks.trackEvent('pass_tier_claimed', { season: s.monetization.season.id, tier, track });
        hooks.showToast(res.ok ? 'Tier resgatado' : (res.error || 'Nada'));
        if (res.ok) { hooks.save(); hooks.refreshEconomy(); renderPass(); }
      } catch (error) {
        const code = String(error?.message || '');
        hooks.showToast(
          code.includes('already') || code.includes('duplicate') ? 'Já resgatado.'
            : code.includes('insufficient_pass') ? 'Pontos insuficientes.'
              : code.includes('premium_not') ? 'Pass Premium necessário.'
                : 'Não foi possível resgatar.',
        );
        renderPass();
      }
      return;
    }
    if (el.dataset.gym) {
      const res = resolveGymBattle(s, el.dataset.gym, hooks.teamPower());
      if (res.win) grantPassPoints(s, 15, 'gym');
      hooks.showToast(res.error ? res.error : (res.win ? 'Vitória!' : 'Derrota'));
      hooks.save(); renderGym();
      return;
    }
    if (el.dataset.elite) {
      const res = resolveEliteBattle(s, el.dataset.elite, hooks.teamPower());
      if (res.win) grantPassPoints(s, 25, 'elite');
      hooks.showToast(res.error ? res.error : (res.win ? 'Elite OK!' : 'Derrota'));
      hooks.save(); renderGym();
      return;
    }
    if (el.dataset.champion) {
      const res = resolveChampionBattle(s, hooks.teamPower());
      if (res.win) grantPassPoints(s, 50, 'champion');
      hooks.showToast(res.error ? res.error : (res.win ? 'Campeão!' : 'Derrota'));
      hooks.save(); renderGym();
      return;
    }
    if (el.dataset.joinClan) {
      try {
        const res = await hooks.joinClanOnServer(el.dataset.joinClan);
        remoteClan = res?.clan || remoteClan;
        remoteClanLoaded = true;
        applyServerEconomySnapshot(res);
        hooks.showToast('Entrou no clã');
        hooks.refreshEconomy(); renderClan();
      } catch (error) {
        const code = String(error?.message || error || '');
        hooks.showToast(code.includes('trainer_level_too_low') ? 'Nv.80+' : code.includes('insufficient_emerald') ? 'Esmeraldas insuficientes.' : 'Não foi possível entrar no clã.');
      }
      return;
    }
    if (el.dataset.clanRank) {
      try {
        const res = await hooks.skipClanRankOnServer(Number(el.dataset.clanRank));
        remoteClan = res?.clan || remoteClan;
        remoteClanLoaded = true;
        applyServerEconomySnapshot(res);
        hooks.showToast(`Rank ${remoteClan?.rank || ''}`.trim());
        hooks.refreshEconomy(); renderClan();
      } catch (error) {
        const code = String(error?.message || error || '');
        hooks.showToast(code.includes('insufficient_funds') ? 'IdleCoin insuficiente.' : 'Não foi possível subir o rank.');
      }
      return;
    }
    if (el.dataset.raidClaim) {
      el.disabled = true;
      try {
        const claim = await hooks.claimRaidRewards(remoteRaid?.id);
        applyServerEconomySnapshot(claim);
        const granted = claim?.reward || {};
        const parts = [];
        if (granted.coins) parts.push(`${money(granted.coins)} IdleCoin`);
        if (granted.emerald) parts.push(`${money(granted.emerald)} Emerald`);
        for (const [slug, qty] of Object.entries(granted.items || {})) if (qty > 0) parts.push(`${qty}× ${prettify(slug)}`);
        hooks.showToast(t('raid.claimOk', { loot: parts.join(' · ') || '—' }));
        remoteRaidLoaded = false;
        hooks.refreshEconomy(); renderRaid();
      } catch (error) {
        const code = String(error?.message || error || '');
        hooks.showToast(
          code.includes('already_claimed') ? t('raid.err.already_claimed')
            : code.includes('raid_still_active') ? t('raid.err.raid_still_active') : t('raid.err.generic'),
        );
        el.disabled = false;
        renderRaid();
      }
      return;
    }
  };
}

export {
  activeBoostMult, BOOST_DEFS, upgradePerks, normalizeUpgrades, normalizeMeta, normalizeMonetization,
  normalizeProfessions, isVip, vipPerks, grantPassPoints, CITY_NPCS,
};
